import React from "react";
import { Stack } from "@mui/material";
import { PageContainer } from "../components/Container/PageContainer";
import { LifeTimePerfromanceSummarySection } from "../template/metric/LifeTimePerfromanceSummarySection";
import { LifetimeKeyMetricChartSection } from "../components/LifetimeAnalytic/LifetimeKeyMetricChartSection";
import { ThreadPerformanceOverview } from "../components/LifetimeAnalytic/ThreadPerformanceOverview";
import { TopPerformanceThreadsSection } from "../components/LifetimeAnalytic/TopPerformanceThreadsSection";

import { saveAs } from "file-saver";
import { useUser } from "../contexts/UserContext";
import { useTopPerformanceData } from "../hooks/useThreadsSelector";
import { motion } from "framer-motion";
import { DataLoadError } from "../components/error/DataLoadError";

// 애니메이션 설정
const sectionVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
      delay: delay,
    },
  }),
};

const LifetimeAnalytic = ({ firstThreadDate }) => {
  // UserContext에서 상태 가져오기
  const { profile, error } = useUser();

  // ThreadsContext에서 전체 스레드 데이터 가져오기 (다운로드용)
  const { lifetimeThreadsList } = useTopPerformanceData();

  // 날짜 범위별 스레드 필터링 함수 (LifetimeKeyMetricChartSection용)
  const getThreadsForDateRange = (startDate, endDate) => {
    if (!lifetimeThreadsList || !Array.isArray(lifetimeThreadsList) || lifetimeThreadsList.length === 0) {
      return [];
    }

    return lifetimeThreadsList.filter((thread) => {
      if (!thread || !thread.timestamp) return false;

      // 스레드 날짜를 UTC 기준으로 정규화
      const threadDate = new Date(thread.timestamp);
      threadDate.setUTCHours(0, 0, 0, 0);
      
      // 시작/종료 날짜를 UTC 기준으로 정규화
      const start = new Date(startDate);
      start.setUTCHours(0, 0, 0, 0);
      
      const end = new Date(endDate);
      end.setUTCHours(23, 59, 59, 999);

      return threadDate >= start && threadDate <= end;
    });
  };

  // JSON 다운로드 함수 (향후 다운로드 기능 추가 시 사용)
  const downloadJSON = () => {
    if (lifetimeThreadsList && lifetimeThreadsList.length > 0) {
      // Filter threads to include only specific fields
      const filteredThreads = lifetimeThreadsList.map(
        ({ text, views, likes, comments, reposts }) => ({
          text,
          views,
          likes,
          comments,
          reposts,
        })
      );

      const jsonData = JSON.stringify(filteredThreads, null, 2);
      const blob = new Blob([jsonData], {
        type: "application/json;charset=utf-8;",
      });
      saveAs(blob, "threadsList.json");
    }
  };

  // 향후 기능 확장을 위해 downloadJSON 함수 준비 (현재는 사용하지 않음)
  // eslint-disable-next-line no-unused-expressions
  downloadJSON;

  // 사용자 인증 에러 처리
  if (error) {
    return (
      <PageContainer>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            height: "60vh",
            px: 3,
          }}
        >
          <DataLoadError title="인증 오류" message={error} showRetry={false} />
        </Stack>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack spacing={8} sx={{ width: '100%', overflowX: 'hidden' }}>
        <LifeTimePerfromanceSummarySection
          firstThreadDate={firstThreadDate}
          profile={profile}
        />
        <LifetimeKeyMetricChartSection
          getThreadsForDateRange={getThreadsForDateRange}
        />
        <TopPerformanceThreadsSection />
        <ThreadPerformanceOverview />
      </Stack>
    </PageContainer>
  );
};

export default LifetimeAnalytic;
